import React, { useState, useEffect } from 'react';
import { HexColorPicker, RgbColorPicker, RgbColor, RgbaColor } from 'react-colorful';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ColorGridPicker from './ColorGridPicker';

interface ColorPickerProps {
  value: string; // Hex string e.g., #RRGGBB or #RRGGBBAA
  onChange: (color: string) => void;
}

// Helper to convert RGB/RGBA object to hex string
const toHex = (color: RgbColor | RgbaColor): string => {
  if ('a' in color && color.a !== undefined && color.a < 1) {
    // It's RGBA, convert to #RRGGBBAA
    const { r, g, b, a } = color;
    const hexA = Math.round(a * 255).toString(16).padStart(2, '0');
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${hexA}`.toUpperCase();
  } else {
    // It's RGB, convert to #RRGGBB
    const { r, g, b } = color as RgbColor;
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
  }
};

// Helper to convert hex string to RGBA object
const hexToRgba = (hex: string): RgbaColor => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex);
  if (!result) return { r: 0, g: 0, b: 0, a: 1 }; // Default to black if invalid hex

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  const a = result[4] ? parseInt(result[4], 16) / 255 : 1;

  return { r, g, b, a };
};

const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange }) => {
  const [r, setR] = useState(0);
  const [g, setG] = useState(0);
  const [b, setB] = useState(0);
  const [a, setA] = useState(100); // 0-100 for percentage
  const [rgbColor, setRgbColor] = useState<RgbColor>({ r: 0, g: 0, b: 0 });

  useEffect(() => {
    const rgba = hexToRgba(value);
    setR(rgba.r);
    setG(rgba.g);
    setB(rgba.b);
    setA(Math.round(rgba.a * 100));
    setRgbColor({ r: rgba.r, g: rgba.g, b: rgba.b });
  }, [value]);

  const handleRgbaChange = (newR: number, newG: number, newB: number, newA: number) => {
    const rgbaColor: RgbaColor = {
      r: Math.max(0, Math.min(255, newR)),
      g: Math.max(0, Math.min(255, newG)),
      b: Math.max(0, Math.min(255, newB)),
      a: Math.max(0, Math.min(100, newA)) / 100,
    };
    onChange(toHex(rgbaColor));
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newHex = e.target.value.toUpperCase();
    if (!newHex.startsWith('#')) {
      newHex = '#' + newHex;
    }
    // Basic validation for hex color length
    if (newHex.match(/^#[0-9A-F]{3}([0-9A-F]{3})?([0-9A-F]{2})?$/i)) {
      onChange(newHex);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div
          className="w-10 h-10 rounded-md border border-input"
          style={{ backgroundColor: value }}
        />
        <div className="flex-1">
          <Label>Hex Color</Label>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">#</span>
            <Input
              value={value.replace(/^#/, '')}
              onChange={handleHexInputChange}
              placeholder="RRGGBB or RRGGBBAA"
              className="flex-1 h-10 px-3 py-2 bg-background border border-input rounded-md"
            />
          </div>
        </div>
      </div>
      
      <Tabs defaultValue="grid" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="grid">Grid</TabsTrigger>
          <TabsTrigger value="spectrum">Spectrum</TabsTrigger>
          <TabsTrigger value="slider">Slider</TabsTrigger>
        </TabsList>
        <TabsContent value="grid" className="p-4 border border-input rounded-md bg-background mt-4">
          <ColorGridPicker
            value={value}
            onChange={onChange}
          />
        </TabsContent>
        <TabsContent value="spectrum" className="p-4 border border-input rounded-md bg-background mt-4">
          <RgbColorPicker
            color={rgbColor} // Use the internal rgbColor state
            onChange={(newRgb) => {
              setRgbColor(newRgb);
              onChange(toHex(newRgb));
            }}
            className="!w-full"
          />
        </TabsContent>
        <TabsContent value="slider" className="p-4 border border-input rounded-md bg-background mt-4 space-y-2">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <Label className="w-16">Red</Label>
              <input
                type="range"
                min="0"
                max="255"
                value={r}
                onChange={(e) => handleRgbaChange(Number(e.target.value), g, b, a)}
                className="flex-grow h-3 rounded-md appearance-none cursor-pointer"
                style={{
                  background: 'linear-gradient(to right, #000, #F00)',
                }}
              />
              <Input 
                value={r} 
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (!isNaN(val)) {
                    handleRgbaChange(Math.max(0, Math.min(255, val)), g, b, a);
                  }
                }}
                className="w-16 text-center text-sm font-mono"
              />
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <Label className="w-16">Green</Label>
              <input
                type="range"
                min="0"
                max="255"
                value={g}
                onChange={(e) => handleRgbaChange(r, Number(e.target.value), b, a)}
                className="flex-grow h-3 rounded-md appearance-none cursor-pointer"
                style={{
                  background: 'linear-gradient(to right, #000, #0F0)',
                }}
              />
              <Input 
                value={g} 
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (!isNaN(val)) {
                    handleRgbaChange(r, Math.max(0, Math.min(255, val)), b, a);
                  }
                }}
                className="w-16 text-center text-sm font-mono"
              />
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <Label className="w-16">Blue</Label>
              <input
                type="range"
                min="0"
                max="255"
                value={b}
                onChange={(e) => handleRgbaChange(r, g, Number(e.target.value), a)}
                className="flex-grow h-3 rounded-md appearance-none cursor-pointer"
                style={{
                  background: 'linear-gradient(to right, #000, #00F)',
                }}
              />
              <Input 
                value={b} 
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (!isNaN(val)) {
                    handleRgbaChange(r, g, Math.max(0, Math.min(255, val)), a);
                  }
                }}
                className="w-16 text-center text-sm font-mono"
              />
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <Label className="w-16">Opacity</Label>
              <input
                type="range"
                min="0"
                max="100"
                value={a}
                onChange={(e) => handleRgbaChange(r, g, b, Number(e.target.value))}
                className="flex-grow h-3 rounded-md appearance-none cursor-pointer border border-input"
                style={{
                  background: `linear-gradient(to right, #000, ${value.substring(0, 7) || '#000000'})`,
                }}
              />
              <Input 
                value={`${a}%`} 
                onChange={(e) => {
                  const val = parseFloat(e.target.value.replace('%', ''));
                  if (!isNaN(val)) {
                    handleRgbaChange(r, g, b, Math.max(0, Math.min(100, val)));
                  }
                }}
                className="w-16 text-center text-sm font-mono"
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ColorPicker; 